'use no memo';
import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, EyeNoneIcon } from '@radix-ui/react-icons';
import { type Column } from '@tanstack/react-table';

import { Button } from '../../components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/dropdownMenu';
import { cn } from '../../utils';

interface DataTableColumnHeaderProps<TData, TValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  column: Column<TData, TValue>;
  title: React.ReactNode;
  hideVisibilityToggle?: boolean;
  /**
   * 'menu' (default) opens Asc / Desc / Hide. 'toggle' sorts on click, cycling
   * ascending, descending, then off (List page template).
   */
  sortMode?: 'menu' | 'toggle';
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  hideVisibilityToggle = false,
  sortMode = 'menu',
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn('text-xs', className)}>{title}</div>;
  }

  if (sortMode === 'toggle') {
    const sorted = column.getIsSorted();
    const next = column.getNextSortingOrder();
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn('-ml-3 h-8 gap-2', className)}
        onClick={column.getToggleSortingHandler()}
        aria-label={`${typeof title === 'string' ? title : 'Column'}: ${
          sorted === 'asc'
            ? 'sorted ascending'
            : sorted === 'desc'
              ? 'sorted descending'
              : 'not sorted'
        }. ${next === 'asc' ? 'Sort ascending' : next === 'desc' ? 'Sort descending' : 'Remove sort'}`}
      >
        <span>{title}</span>
        {sorted === 'desc' ? (
          <ArrowDownIcon className="h-4 w-4" aria-hidden />
        ) : sorted === 'asc' ? (
          <ArrowUpIcon className="h-4 w-4" aria-hidden />
        ) : (
          <CaretSortIcon className="h-4 w-4" aria-hidden />
        )}
      </Button>
    );
  }

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="data-[state=open]:bg-accent -ml-3 h-8">
            <span>{title}</span>
            {column.getIsSorted() === 'desc' ? (
              <ArrowDownIcon className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === 'asc' ? (
              <ArrowUpIcon className="ml-2 h-4 w-4" />
            ) : (
              <CaretSortIcon className="ml-2 h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUpIcon className="text-muted-foreground/70 mr-2 h-3.5 w-3.5" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDownIcon className="text-muted-foreground/70 mr-2 h-3.5 w-3.5" />
            Desc
          </DropdownMenuItem>
          {!hideVisibilityToggle && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                <EyeNoneIcon className="text-muted-foreground/70 mr-2 h-3.5 w-3.5" />
                Hide
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
